import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { PUBLIC_SUBJECT_ROUTE } from "../../services/graphql/queriesMutations";
import type { GistType, SubjectType } from "../../services/types";
import { buildGistUrl, buildSubjectUrl } from "../../utils/publicUrls";
import { absoluteBrowserUrl, applyPublicSeoMeta, copyToClipboard, getDisplayName, getExcerpt } from "./publicPageUtils";
import styles from "./PublicPages.module.css";

type PublicSubjectRouteData = {
  publicSubjectRoute: {
    canonicalUrl: string;
    shouldRedirect: boolean;
    subject: SubjectType;
  };
};

const PublicSubjectPage: React.FC = () => {
  const { subjectSlug = "" } = useParams();
  const navigate = useNavigate();
  const { data, loading, error } = useQuery<PublicSubjectRouteData>(PUBLIC_SUBJECT_ROUTE, {
    variables: { subjectSlug },
    skip: !subjectSlug,
  });

  const route = data?.publicSubjectRoute;
  const subject = route?.subject;
  const [copyStatus, setCopyStatus] = useState<string>("");

  useEffect(() => {
    if (route?.shouldRedirect && route.canonicalUrl) {
      navigate(route.canonicalUrl, { replace: true });
    }
  }, [navigate, route]);

  useEffect(() => {
    if (!subject || !route?.canonicalUrl) return;
    applyPublicSeoMeta({
      title: subject.seoTitle || subject.title,
      description: subject.seoDescription,
      canonicalPath: route.canonicalUrl,
      indexable: subject.visibility === "PUBLIC",
    });
  }, [route, subject]);

  const handleCopy = async () => {
    if (!route?.canonicalUrl) return;
    const ok = await copyToClipboard(absoluteBrowserUrl(route.canonicalUrl));
    setCopyStatus(ok ? "Copied" : "Copy failed");
    window.setTimeout(() => setCopyStatus(""), 1600);
  };

  const childSubjects = useMemo(() => {
    const children = subject?.subjects || [];
    return children
      .filter((child) => child.visibility === "PUBLIC")
      .slice()
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [subject]);

  const rootGists = useMemo(() => {
    const gists = subject?.gists || [];
    return gists
      .filter((gist) => gist.status === "PUBLISHED" && gist.visibility === "PUBLIC" && gist.parentId === null)
      .slice()
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [subject]);

  if (loading) {
    return <div className={styles.page}><main className={styles.shell}>Loading public subject…</main></div>;
  }

  if (error || !subject) {
    return (
      <div className={styles.page}>
        <main className={styles.shell}>
          <div className={styles.errorBox}>Public subject not found.</div>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <main className={styles.shell}>
        <section className={styles.hero}>
          <p className={styles.eyebrow}>Public subject · {subject.kind || "DISCUSSION"}</p>
          <h1 className={styles.title}>{subject.seoTitle || subject.title}</h1>
          {subject.seoDescription ? <p className={styles.subtitle}>{subject.seoDescription}</p> : null}
          <div className={styles.metaRow}>
            <span className={styles.pill}>/{subject.slug}--{subject.publicId}</span>
            <span className={styles.pill}>{subject.visibility || "PUBLIC"}</span>
            <span className={styles.pill}>By {getDisplayName(subject.user)}</span>
          </div>
        </section>

        <div className={styles.grid}>
          <div className={styles.mainStack}>
            <section className={styles.card}>
              <h2 className={styles.sectionTitle}>Child subjects</h2>
              <div className={styles.gistList}>
                {childSubjects.length ? (
                  childSubjects.map((child: SubjectType) => (
                    <Link key={child.id} className={styles.gistLink} to={buildSubjectUrl(child)}>
                      <h3 className={styles.gistTitle}>{child.title}</h3>
                      {child.seoDescription ? <p className={styles.gistExcerpt}>{child.seoDescription}</p> : null}
                      <p className={styles.smallText}>{child.kind || "DISCUSSION"} · /{child.slug}--{child.publicId}</p>
                    </Link>
                  ))
                ) : (
                  <p className={styles.empty}>No public child subjects yet.</p>
                )}
              </div>
            </section>

            <section className={styles.card}>
              <h2 className={styles.sectionTitle}>Public gists</h2>
              <div className={styles.gistList}>
                {rootGists.length ? (
                  rootGists.map((gist: GistType) => (
                    <Link key={gist.id} className={styles.gistLink} to={buildGistUrl(subject, gist)}>
                      <h3 className={styles.gistTitle}>{gist.title}</h3>
                      <p className={styles.gistExcerpt}>{getExcerpt(gist)}</p>
                    </Link>
                  ))
                ) : (
                  <p className={styles.empty}>No published root gists yet.</p>
                )}
              </div>
            </section>
          </div>

          <aside className={styles.sideCard}>
            <h2 className={styles.sectionTitle}>Canonical URL</h2>
            <p className={styles.smallText}>{route?.canonicalUrl}</p>
            <button type="button" className={styles.copyButton} onClick={handleCopy}>Copy public link</button>
            {copyStatus ? <p className={styles.copyStatus}>{copyStatus}</p> : null}
            <hr />
            <div className={styles.linkStack}>
              <Link to="/subject-single">Open workspace</Link>
              <Link to="/navigator">Open navigator</Link>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default PublicSubjectPage;
